import React from 'react';
import styled from 'styled-components';

import {baseUrlByEnvironment} from '../../api/http';
import {
  cancelRentalPaymentForTesting,
  createIdempotencyKey,
  createRentalCharge,
  deleteRentalDocument,
  getProtectedFile,
  getRental,
  getRentalDocuments,
  getRentalTestTools,
  getRentals,
  overrideRentalForTesting,
  postRentalAction,
  retryRentalCharge,
  uploadRentalDocument,
} from '../../api/rental';
import MapWithMarker from '../common/MapWithMarker';
import Layout from './Layout';
import {
  ActionButton,
  Actions,
  Badge,
  Card,
  CardTitle,
  DefinitionList,
  ErrorText,
  Field,
  FieldGrid,
  Grid,
  Input,
  List,
  ListButton,
  Scroll,
  Select,
  State,
  SuccessText,
  Table,
  Textarea,
} from './styles';

const DEFAULT_POINT = {lat: 44.952117, lng: 34.102417};

/** Тестовые инструменты показываются только против dev-API и только если сервер их включил. */
const isDevApi = baseUrlByEnvironment() === 'https://api.drvdev.ru/';

/** ISO-дата в значение для input[type=datetime-local] в местном времени. */
function toLocalInput(value) {
  if (!value) { return ''; }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) { return ''; }
  const pad = number => String(number).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** Центр карты-пикера: введённые координаты, иначе координаты автомобиля. */
function pointFor(values, rental) {
  const lat = Number(values.lat);
  const lng = Number(values.lng);
  if (values.lat !== '' && values.lng !== '' && Number.isFinite(lat) && Number.isFinite(lng)) {
    return {lat, lng};
  }
  const vehicle = rental && rental.vehicle && rental.vehicle.coordinates;
  return vehicle ? {lat: Number(vehicle.lat), lng: Number(vehicle.lng)} : DEFAULT_POINT;
}

function depositRemainderOf(rental) {
  return rental.deposit_amount - rental.deposit_refunded_amount - rental.deposit_withheld_amount;
}

const rentalStatuses = {
  payment_pending: 'Оплата',
  reserved: 'Забронирована',
  active: 'Активна',
  inspection: 'Осмотр',
  completed: 'Завершена',
  canceled: 'Отменена',
  expired: 'Истекла',
  payment_failed: 'Ошибка оплаты',
};

const depositStatuses = {
  none: 'не удерживался',
  held: 'удержан',
  refund_pending: 'возврат в обработке',
  refunded: 'возвращён',
  partially_refunded: 'возвращён частично',
  forfeited: 'удержан в пользу компании',
};

const paymentStatuses = {
  pending: 'Ожидает операции',
  prepaid: 'Предоплачена',
  refund_pending: 'Возврат в обработке',
  settled: 'Расчёт завершён',
  debt: 'Есть долг',
};

const chargeCategories = {
  cleaning: 'Мойка/химчистка',
  refueling: 'Дозаправка/зарядка',
  traffic_fine: 'Штраф ГИБДД',
  damage: 'Повреждение',
  overdue: 'Просрочка возврата',
  mileage_overrun: 'Перерасход пробега',
  other: 'Другое',
};

const documentKinds = {
  contract: 'Договор',
  act_handover: 'Акт выдачи',
  act_return: 'Акт возврата',
  insurance: 'Страховка',
  receipt: 'Чек',
  handover_photo: 'Кадр при выдаче',
  return_photo: 'Кадр при возврате',
  other: 'Другое',
};

const formatDate = value => value ? new Date(value).toLocaleString('ru-RU') : '—';
const money = value => `${(Number(value || 0) / 100).toLocaleString('ru-RU')} ₽`;

function ProtectedFileButton({authorization, fileId, label = 'Открыть доказательство'}) {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const open = async () => {
    setLoading(true);
    setError('');
    try {
      const blob = await getProtectedFile(authorization, fileId);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  };
  return (
    <span>
      <InlineButton type="button" onClick={open} disabled={loading}>{loading ? 'Загрузка…' : label}</InlineButton>
      {error && <InlineError>{error}</InlineError>}
    </span>
  );
}

function Timeline({rental}) {
  const events = [
    ['Бронь создана', rental.created_at],
    ['Аренда началась', rental.started_at],
    ['Автомобиль возвращён', rental.returned_at],
    ['Осмотр завершён', rental.inspected_at],
    ['Бронь отменена', rental.canceled_at],
  ].filter(event => event[1]);
  return (
    <TimelineList>
      {events.map(([label, date]) => (
        <TimelineItem key={`${label}-${date}`}><TimelineDot /><div><b>{label}</b><small>{formatDate(date)}</small></div></TimelineItem>
      ))}
    </TimelineList>
  );
}

function RentalsContent({authorization}) {
  const [rentals, setRentals] = React.useState([]);
  const [filter, setFilter] = React.useState('');
  const [selectedId, setSelectedId] = React.useState('');
  const [rental, setRental] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [detailsLoading, setDetailsLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');
  const [message, setMessage] = React.useState('');
  const commandKeys = React.useRef({});
  const [support, setSupport] = React.useState({reason: '', lat: '', lng: '', refund_rub: ''});
  const [deposit, setDeposit] = React.useState({amount_rub: '', reason: ''});
  const [inspection, setInspection] = React.useState({lat: '', lng: '', vehicle_status: 'available', comment: ''});
  const [charge, setCharge] = React.useState({
    category: 'cleaning',
    amount_rub: '',
    comment: '',
    source: 'deposit',
    file: null,
  });
  const [handover, setHandover] = React.useState({mileage_km: '', fuel_level: '', comment: ''});
  const [testTools, setTestTools] = React.useState(null);
  const [override, setOverride] = React.useState({status: '', payment_status: '', starts_at: '', ends_at: ''});
  const [documents, setDocuments] = React.useState([]);
  const [documentForm, setDocumentForm] = React.useState({
    kind: 'contract',
    title: '',
    visible_to_client: true,
    file: null,
  });

  const loadList = React.useCallback(() => {
    setLoading(true);
    setError('');
    return getRentals(authorization, filter ? {status: filter} : {})
      .then(data => setRentals(Array.isArray(data) ? data : []))
      .catch(requestError => setError(requestError.message))
      .finally(() => setLoading(false));
  }, [authorization, filter]);

  const loadDetails = React.useCallback(id => {
    if (!id) return Promise.resolve();
    setDetailsLoading(true);
    setError('');
    return getRental(authorization, id)
      .then(data => {
        setRental(data);
        const point = data.return_coordinates || (data.vehicle && data.vehicle.coordinates) || {};
        setSupport(current => ({...current, lat: point.lat || '', lng: point.lng || ''}));
        setInspection(current => ({...current, lat: point.lat || '', lng: point.lng || ''}));
        setOverride({
          status: data.status || '',
          payment_status: data.payment_status || '',
          starts_at: toLocalInput(data.starts_at),
          ends_at: toLocalInput(data.ends_at),
        });
      })
      .catch(requestError => setError(requestError.message))
      .finally(() => setDetailsLoading(false));
  }, [authorization]);

  const loadDocuments = React.useCallback(id => {
    if (!id) { setDocuments([]); return Promise.resolve(); }
    return getRentalDocuments(authorization, id)
      .then(data => setDocuments(Array.isArray(data) ? data : []))
      .catch(() => setDocuments([]));
  }, [authorization]);

  React.useEffect(() => {
    if (!isDevApi || !authorization) { return; }
    getRentalTestTools(authorization)
      .then(data => setTestTools(data && data.enabled ? data : null))
      .catch(() => setTestTools(null));
  }, [authorization]);

  const applyOverride = async () => {
    if (!rental) { return; }
    setSaving(true);
    setError('');
    setMessage('');
    try {
      await overrideRentalForTesting(authorization, rental.id, {
        status: override.status || null,
        payment_status: override.payment_status || null,
        starts_at: override.starts_at ? new Date(override.starts_at).toISOString() : null,
        ends_at: override.ends_at ? new Date(override.ends_at).toISOString() : null,
      });
      setMessage('Тестовые изменения применены');
      await loadDetails(rental.id);
      await loadList();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  const cancelPaymentForTest = async paymentId => {
    if (!rental || !window.confirm('Отменить платёж в базе без обращения к эквайеру?')) { return; }
    setSaving(true);
    setError('');
    setMessage('');
    try {
      await cancelRentalPaymentForTesting(authorization, rental.id, paymentId);
      setMessage('Платёж помечен отменённым');
      await loadDetails(rental.id);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  React.useEffect(() => { loadList(); }, [loadList]);
  React.useEffect(() => { loadDetails(selectedId); }, [loadDetails, selectedId]);
  React.useEffect(() => { loadDocuments(selectedId); }, [loadDocuments, selectedId]);

  const selectRental = item => {
    setSelectedId(item.id);
    setMessage('');
    setError('');
    commandKeys.current = {};
  };

  const runAction = async (action, payload, confirmation) => {
    if (confirmation && !window.confirm(confirmation)) return;
    setSaving(true);
    setError('');
    setMessage('');
    if (!commandKeys.current[action]) commandKeys.current[action] = createIdempotencyKey(`rental-${action}`);
    try {
      await postRentalAction(authorization, selectedId, action, payload, commandKeys.current[action]);
      commandKeys.current[action] = '';
      setMessage('Действие выполнено. Карточка аренды обновлена.');
      await Promise.all([loadDetails(selectedId), loadList()]);
    } catch (requestError) {
      setError(`${requestError.message}. Повторная попытка использует тот же ключ команды.`);
    } finally {
      setSaving(false);
    }
  };

  const createCharge = async event => {
    event.preventDefault();
    if (!charge.file) {
      setError('Добавьте минимум одно фото или скан доказательства.');
      return;
    }
    const confirmation = charge.source === 'deposit'
      ? `Зачесть ${charge.amount_rub} ₽ из остатка депозита? Возврат клиенту уменьшится на эту сумму.`
      : `Создать начисление ${charge.amount_rub} ₽ и сразу попытаться списать его с карты?`;
    if (!window.confirm(confirmation)) return;
    setSaving(true);
    setError('');
    if (!commandKeys.current.charge) commandKeys.current.charge = createIdempotencyKey('rental-charge');
    try {
      await createRentalCharge(authorization, selectedId, {
        category: charge.category,
        amount: Math.round(Number(charge.amount_rub) * 100),
        comment: charge.comment.trim(),
        source: charge.source,
      }, charge.file, commandKeys.current.charge);
      commandKeys.current.charge = '';
      setCharge({category: 'cleaning', amount_rub: '', comment: '', source: 'deposit', file: null});
      setMessage(charge.source === 'deposit'
        ? 'Начисление зачтено из депозита; возврат клиенту уменьшен.'
        : 'Начисление создано; результат списания показан в таблице.');
      await loadDetails(selectedId);
    } catch (requestError) {
      setError(`${requestError.message}. Не меняйте форму: повтор будет отправлен с тем же ключом.`);
    } finally {
      setSaving(false);
    }
  };

  /**
   * Выдача и приём: показания обязательны, потому что перерасчёт по пробегу и
   * претензия по топливу опираются на пару значений, а не на одно.
   */
  const runReadingsAction = async action => {
    const mileage = Number(handover.mileage_km);
    const fuel = Number(handover.fuel_level);
    if (!Number.isInteger(mileage) || mileage < 0) {
      setError('Пробег указывается целым числом километров.');
      return;
    }
    if (!Number.isInteger(fuel) || fuel < 0 || fuel > 100) {
      setError('Уровень топлива указывается в процентах от 0 до 100.');
      return;
    }
    await runAction(
      action,
      {mileage_km: mileage, fuel_level: fuel, comment: handover.comment.trim() || null},
      action === 'handover'
        ? 'Выдать автомобиль клиенту и открыть аренду?'
        : 'Принять автомобиль с указанными показаниями?',
    );
    setHandover({mileage_km: '', fuel_level: '', comment: ''});
  };

  const uploadDocument = async event => {
    event.preventDefault();
    if (!documentForm.file || !documentForm.title.trim()) {
      setError('Выберите файл и укажите название документа.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await uploadRentalDocument(authorization, selectedId, {
        kind: documentForm.kind,
        title: documentForm.title.trim(),
        visible_to_client: documentForm.visible_to_client ? 'true' : 'false',
      }, documentForm.file);
      setDocumentForm({kind: 'contract', title: '', visible_to_client: true, file: null});
      setMessage('Документ загружен.');
      await loadDocuments(selectedId);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  const removeDocument = async document => {
    const reason = window.prompt(`Основание для удаления «${document.title}»:`);
    if (!reason || !reason.trim()) { return; }
    setSaving(true);
    setError('');
    try {
      await deleteRentalDocument(authorization, selectedId, document.id, reason.trim());
      setMessage('Документ удалён из списка. Файл остаётся в хранилище с аудитом.');
      await loadDocuments(selectedId);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  // Свободный остаток депозита: сколько ещё можно зачесть в счёт начислений.
  const depositFree = rental
    ? Math.max(0, rental.deposit_amount
      - rental.deposit_refunded_amount
      - rental.deposit_withheld_amount)
    : 0;

  const retryCharge = async chargeId => {
    if (!window.confirm('Повторить списание по сохранённой карте аренды?')) return;
    const keyName = `retry-${chargeId}`;
    if (!commandKeys.current[keyName]) commandKeys.current[keyName] = createIdempotencyKey('rental-charge-retry');
    setSaving(true);
    setError('');
    try {
      await retryRentalCharge(authorization, chargeId, commandKeys.current[keyName]);
      commandKeys.current[keyName] = '';
      setMessage('Повтор списания выполнен.');
      await loadDetails(selectedId);
    } catch (requestError) {
      setError(`${requestError.message}. Повторная попытка использует тот же ключ.`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Grid>
      <Card>
        <CardTitle>Список аренд</CardTitle>
        <Field>
          Статус
          <Select value={filter} onChange={event => { setFilter(event.target.value); setSelectedId(''); setRental(null); }}>
            <option value="">Все аренды</option>
            {Object.keys(rentalStatuses).map(status => <option key={status} value={status}>{rentalStatuses[status]}</option>)}
          </Select>
        </Field>
        <div style={{height: 16}} />
        {loading && <State>Загрузка аренд…</State>}
        {!loading && error && !rental && <State error>{error}</State>}
        {!loading && !error && rentals.length === 0 && <State>Аренд с таким статусом пока нет.</State>}
        {!loading && rentals.length > 0 && (
          <List>
            {rentals.map(item => (
              <ListButton key={item.id} active={item.id === selectedId} onClick={() => selectRental(item)}>
                <strong>
                  {item.vehicle ? `${item.vehicle.brand} ${item.vehicle.model}` : 'Автомобиль'}
                </strong>
                <RentalMeta>
                  {item.vehicle && item.vehicle.license_plate} · {item.phone || item.user_id}
                </RentalMeta>
                <Badge danger={item.payment_status === 'debt'} success={item.status === 'completed'}>
                  {rentalStatuses[item.status] || item.status} · {paymentStatuses[item.payment_status] || item.payment_status}
                </Badge>
              </ListButton>
            ))}
          </List>
        )}
      </Card>

      <Card>
        <CardTitle>Карточка аренды</CardTitle>
        {!selectedId && <State>Выберите аренду в списке слева.</State>}
        {selectedId && detailsLoading && <State>Загрузка карточки…</State>}
        {selectedId && !detailsLoading && error && !rental && <State error>{error}</State>}
        {rental && (
          <>
            <HeaderBadges>
              <Badge success={rental.status === 'completed'}>{rentalStatuses[rental.status] || rental.status}</Badge>
              <Badge danger={rental.payment_status === 'debt'} success={rental.payment_status === 'settled'}>
                {paymentStatuses[rental.payment_status] || rental.payment_status}
              </Badge>
            </HeaderBadges>
            {(rental.status === 'reserved'
              || rental.status === 'inspection'
              || rental.status === 'active'
              || (rental.status === 'completed' && depositRemainderOf(rental) > 0)) && (
              <NextStep>
                <CardTitle>Следующий шаг</CardTitle>
                {rental.status === 'active' && (
                  <Hint>
                    Автомобиль у клиента до {formatDate(rental.ends_at)}. Возврат начинает клиент
                    из приложения; при необходимости — «Принудительно вернуть» в операциях поддержки.
                  </Hint>
                )}
                {(rental.status === 'reserved'
                  || (rental.status === 'inspection' && !rental.return_accepted_at)) && (
                  <NextStepPart>
                    <CardTitle>{rental.status === 'reserved' ? 'Выдача автомобиля' : 'Приём автомобиля'}</CardTitle>
                    <FieldGrid style={{marginTop: 16}}>
                      <Field>
                        Пробег, км *
                        <Input
                          type="number"
                          min="0"
                          step="1"
                          value={handover.mileage_km}
                          onChange={event => setHandover({...handover, mileage_km: event.target.value})}
                        />
                      </Field>
                      <Field>
                        Топливо/заряд, % *
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          step="1"
                          value={handover.fuel_level}
                          onChange={event => setHandover({...handover, fuel_level: event.target.value})}
                        />
                      </Field>
                    </FieldGrid>
                    <Field style={{marginTop: 14}}>
                      Замечания по состоянию
                      <Textarea
                        short
                        value={handover.comment}
                        onChange={event => {
                          setHandover({...handover, comment: event.target.value});
                          commandKeys.current.handover = '';
                          commandKeys.current['accept-return'] = '';
                        }}
                      />
                    </Field>
                    <Actions>
                      {rental.status === 'reserved' && (
                        <ActionButton
                          primary
                          disabled={saving || !handover.mileage_km || !handover.fuel_level}
                          onClick={() => runReadingsAction('handover')}
                        >
                          Выдать автомобиль
                        </ActionButton>
                      )}
                      {rental.status === 'inspection' && !rental.return_accepted_at && (
                        <ActionButton
                          primary
                          disabled={saving || !handover.mileage_km || !handover.fuel_level}
                          onClick={() => runReadingsAction('accept-return')}
                        >
                          Принять автомобиль
                        </ActionButton>
                      )}
                    </Actions>
                  </NextStepPart>
                )}
                {rental.status === 'inspection' && (
                  <NextStepPart>
                    <CardTitle>Завершить осмотр</CardTitle>
                    <MapBox aria-label="Карта точки возврата">
                      <MapWithMarker
                        center={pointFor(inspection, rental)}
                        handleMarkerChange={map => setInspection({...inspection, lat: map.center[0], lng: map.center[1]})}
                      />
                    </MapBox>
                    <MapHelp>Перемещайте карту: точка возврата фиксируется в центре маркера.</MapHelp>
                    <FieldGrid style={{marginTop: 14}}>
                      <Field>Итоговая широта *<Input required type="number" step="0.000001" value={inspection.lat} onChange={event => setInspection({...inspection, lat: event.target.value})} /></Field>
                      <Field>Итоговая долгота *<Input required type="number" step="0.000001" value={inspection.lng} onChange={event => setInspection({...inspection, lng: event.target.value})} /></Field>
                      <Field>Статус автомобиля<Select value={inspection.vehicle_status} onChange={event => setInspection({...inspection, vehicle_status: event.target.value})}><option value="available">Вернуть в прокат</option><option value="maintenance">На обслуживание</option></Select></Field>
                    </FieldGrid>
                    <Field style={{marginTop: 14}}>Комментарий<Textarea short value={inspection.comment} onChange={event => { setInspection({...inspection, comment: event.target.value}); commandKeys.current.inspection = ''; }} /></Field>
                    <Actions><ActionButton primary disabled={saving || !inspection.lat || !inspection.lng} onClick={() => runAction('inspection', {coordinates: {lat: Number(inspection.lat), lng: Number(inspection.lng)}, vehicle_status: inspection.vehicle_status, comment: inspection.comment || null}, 'Завершить осмотр с выбранным статусом автомобиля?')}>Завершить осмотр</ActionButton></Actions>
                  </NextStepPart>
                )}
                {rental.status === 'completed' && depositRemainderOf(rental) > 0 && (
                  <NextStepPart>
                    <CardTitle>Остаток депозита</CardTitle>
                    <Hint>
                      После расчёта клиенту причитается {money(depositRemainderOf(rental))}.
                      Частичный возврат с причиной — в разделе «Депозит» ниже.
                    </Hint>
                    <Actions>
                      <ActionButton
                        primary
                        disabled={saving}
                        onClick={() => runAction(
                          'deposit-refund',
                          {amount: depositRemainderOf(rental), reason: null},
                          'Вернуть клиенту весь остаток депозита?'
                        )}
                      >
                        Вернуть остаток депозита
                      </ActionButton>
                    </Actions>
                  </NextStepPart>
                )}
              </NextStep>
            )}
            <DefinitionList>
              <dt>ID аренды</dt><dd>{rental.id}</dd>
              <dt>Пользователь</dt>
              <dd>
                <ClientLink href={`/rental/clients/${rental.user_id}`}>{rental.user_id}</ClientLink>
              </dd>
              <dt>Автомобиль</dt><dd>{rental.vehicle ? `${rental.vehicle.brand} ${rental.vehicle.model}, ${rental.vehicle.license_plate}` : rental.vehicle_id}</dd>
              <dt>Тариф</dt><dd>{money(rental.daily_rate)} × {rental.selected_days} суток</dd>
              <dt>Предоплата</dt><dd>{money(rental.prepaid_amount)}</dd>
              <dt>Итоговая база</dt><dd>{rental.base_amount == null ? '—' : money(rental.base_amount)}</dd>
              <dt>Возврат</dt><dd>{money(rental.refund_amount)}</dd>
              <dt>Депозит</dt><dd>{money(rental.deposit_amount)} · {depositStatuses[rental.deposit_status] || rental.deposit_status}</dd>
              <dt>Возвращено депозита</dt><dd>{money(rental.deposit_refunded_amount)}</dd>
              <dt>Удержано из депозита</dt>
              <dd>{money(rental.deposit_withheld_amount)} — в счёт начислений</dd>
              <dt>Просрочка</dt>
              <dd>
                {rental.overdue_hours > 0
                  ? `${rental.overdue_hours} ч · ${money(rental.overdue_amount)}`
                  : '—'}
              </dd>
              <dt>Перерасход пробега</dt>
              <dd>
                {rental.mileage_overrun_km > 0
                  ? `${rental.mileage_overrun_km} км · ${money(rental.mileage_overrun_amount)}`
                  : '—'}
              </dd>
              <dt>Удержано при отмене</dt><dd>{money(rental.cancellation_fee_amount)}</dd>
              <dt>Перерасход</dt><dd>{money(rental.overrun_amount)}</dd>
              <dt>Долг</dt><dd>{money(rental.debt_amount)}</dd>
              <dt>Точка возврата</dt><dd>{rental.return_coordinates ? `${rental.return_coordinates.lat}, ${rental.return_coordinates.lng}` : '—'}</dd>
              <dt>Комментарий осмотра</dt><dd>{rental.inspection_comment || '—'}</dd>
            </DefinitionList>

            <Section><CardTitle>Таймлайн</CardTitle><Timeline rental={rental} /></Section>

            <Section>
              <CardTitle>Выдача и приём</CardTitle>
              <Hint>
                Показания снимаются дважды: при выдаче и при приёме. Без пары значений
                перерасчёт по пробегу и претензия по топливу недоказуемы, поэтому пробег
                при возврате не может быть меньше, чем при выдаче.
              </Hint>
              <DefinitionList>
                <dt>Выдан</dt>
                <dd>
                  {rental.handover_at
                    ? `${formatDate(rental.handover_at)} · ${rental.handover_mileage_km} км · топливо ${rental.handover_fuel_level}%`
                    : '—'}
                </dd>
                <dt>Принят</dt>
                <dd>
                  {rental.return_accepted_at
                    ? `${formatDate(rental.return_accepted_at)} · ${rental.return_mileage_km} км · топливо ${rental.return_fuel_level}%`
                    : '—'}
                </dd>
                <dt>Пробег за аренду</dt>
                <dd>
                  {rental.return_mileage_km != null && rental.handover_mileage_km != null
                    ? `${rental.return_mileage_km - rental.handover_mileage_km} км`
                    : '—'}
                </dd>
              </DefinitionList>

            </Section>

            <Section>
              <CardTitle>Документы аренды</CardTitle>
              <Hint>
                Клиент видит только документы, помеченные видимыми. Кадры осмотра
                остаются внутренними, пока по ним нет решения.
              </Hint>
              {documents.length === 0 && <State>Документов пока нет.</State>}
              {documents.length > 0 && (
                <Scroll>
                  <Table minWidth="760px">
                    <thead>
                      <tr>
                        <th>Тип</th><th>Название</th><th>Выдан</th>
                        <th>Клиенту</th><th>Файл</th><th>Действие</th>
                      </tr>
                    </thead>
                    <tbody>
                      {documents.map(document => (
                        <tr key={document.id}>
                          <td>{documentKinds[document.kind] || document.kind}</td>
                          <td>{document.title}</td>
                          <td>{formatDate(document.issued_at)}</td>
                          <td>{document.visible_to_client ? 'виден' : 'внутренний'}</td>
                          <td>
                            <ProtectedFileButton
                              authorization={authorization}
                              fileId={document.file_id}
                              label="Открыть"
                            />
                          </td>
                          <td>
                            <InlineButton
                              type="button"
                              disabled={saving}
                              onClick={() => removeDocument(document)}
                            >
                              Удалить
                            </InlineButton>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Scroll>
              )}

              <ChargeForm onSubmit={uploadDocument}>
                <CardTitle>Новый документ</CardTitle>
                <FieldGrid>
                  <Field>
                    Тип *
                    <Select
                      value={documentForm.kind}
                      onChange={event => setDocumentForm({...documentForm, kind: event.target.value})}
                    >
                      {Object.keys(documentKinds).map(kind => (
                        <option key={kind} value={kind}>{documentKinds[kind]}</option>
                      ))}
                    </Select>
                  </Field>
                  <Field>
                    Название *
                    <Input
                      required
                      value={documentForm.title}
                      onChange={event => setDocumentForm({...documentForm, title: event.target.value})}
                    />
                  </Field>
                </FieldGrid>
                <Field style={{marginTop: 14}}>
                  Файл (JPEG, PNG или PDF) *
                  <Input
                    required
                    type="file"
                    accept="image/jpeg,image/png,application/pdf"
                    onChange={event => setDocumentForm({
                      ...documentForm,
                      file: event.target.files[0] || null,
                    })}
                  />
                </Field>
                <Field style={{marginTop: 14}}>
                  <label>
                    <input
                      type="checkbox"
                      checked={documentForm.visible_to_client}
                      onChange={event => setDocumentForm({
                        ...documentForm,
                        visible_to_client: event.target.checked,
                      })}
                    />
                    {' '}Показывать клиенту в приложении
                  </label>
                </Field>
                <Actions>
                  <ActionButton
                    type="submit"
                    primary
                    disabled={saving || !documentForm.file || !documentForm.title.trim()}
                  >
                    Загрузить документ
                  </ActionButton>
                </Actions>
              </ChargeForm>
            </Section>

            <Section>
              <CardTitle>Платежи</CardTitle>
              {(!rental.payments || rental.payments.length === 0) && <State>Платежей пока нет.</State>}
              {rental.payments && rental.payments.length > 0 && (
                <Scroll><Table minWidth="780px"><thead><tr><th>Дата</th><th>Назначение</th><th>Провайдер</th><th>Статус</th><th>Сумма</th></tr></thead>
                  <tbody>{rental.payments.map(payment => <tr key={payment.id}><td>{formatDate(payment.created_at)}</td><td>{payment.purpose}</td><td>{payment.provider}</td><td>{payment.status}</td><td>{money(payment.amount)}</td></tr>)}</tbody>
                </Table></Scroll>
              )}
            </Section>

            <Section>
              <CardTitle>Операции поддержки</CardTitle>
              <Field>Причина/комментарий<Textarea short value={support.reason} onChange={event => { setSupport({...support, reason: event.target.value}); commandKeys.current = {}; }} /></Field>
              {(rental.status === 'active' || rental.status === 'inspection') && (
                <>
                <MapBox style={{marginTop: 14}} aria-label="Карта точки принудительного возврата">
                  <MapWithMarker
                    center={pointFor(support, rental)}
                    handleMarkerChange={map => setSupport({...support, lat: map.center[0], lng: map.center[1]})}
                  />
                </MapBox>
                <MapHelp>Точка возврата для принудительного завершения — в центре маркера.</MapHelp>
                <FieldGrid style={{marginTop: 14}}>
                  <Field>Широта<Input type="number" step="0.000001" value={support.lat} onChange={event => setSupport({...support, lat: event.target.value})} /></Field>
                  <Field>Долгота<Input type="number" step="0.000001" value={support.lng} onChange={event => setSupport({...support, lng: event.target.value})} /></Field>
                </FieldGrid>
                </>
              )}
              <Field style={{marginTop: 14}}>Ручной возврат, ₽<Input type="number" min="0.01" step="0.01" value={support.refund_rub} onChange={event => setSupport({...support, refund_rub: event.target.value})} /></Field>
              <Actions>
                {rental.status === 'reserved' && <ActionButton danger disabled={saving || !support.reason.trim()} onClick={() => runAction('cancel', {reason: support.reason.trim()}, 'Отменить бронь и запустить полный возврат?')}>Принудительно отменить</ActionButton>}
                {rental.status === 'active' && <ActionButton danger disabled={saving || !support.lat || !support.lng} onClick={() => runAction('finish', {reason: support.reason || 'manager-finish', coordinates: {lat: Number(support.lat), lng: Number(support.lng)}}, 'Зафиксировать возврат автомобиля по указанным координатам?')}>Принудительно вернуть</ActionButton>}
                <ActionButton disabled={saving || !support.refund_rub || !support.reason.trim()} onClick={() => runAction('refund', {amount: Math.round(Number(support.refund_rub) * 100), reason: support.reason.trim()}, 'Выполнить ручной возврат указанной суммы?')}>Оформить возврат</ActionButton>
              </Actions>
            </Section>

            {rental.deposit_amount > 0 && rental.deposit_status !== 'none' && (
              <Section>
                <CardTitle>Депозит</CardTitle>
                <Hint>
                  Депозит списан отдельным платежом, поэтому его возврат не затрагивает
                  деньги за аренду. Просрочка и перерасход пробега уже удержаны из него;
                  удерживать сверх этого можно только при подтверждённом начислении
                  с доказательствами.
                </Hint>
                <DefinitionList>
                  <dt>Внесено</dt><dd>{money(rental.deposit_amount)}</dd>
                  <dt>Возвращено</dt><dd>{money(rental.deposit_refunded_amount)}</dd>
                  <dt>Удержано в счёт начислений</dt>
                  <dd>{money(rental.deposit_withheld_amount)}</dd>
                  <dt>Осталось вернуть</dt>
                  <dd>
                    {money(rental.deposit_amount
                      - rental.deposit_refunded_amount
                      - rental.deposit_withheld_amount)}
                  </dd>
                </DefinitionList>
                <FieldGrid style={{marginTop: 14}}>
                  <Field>
                    Сумма возврата, ₽
                    <Input
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={deposit.amount_rub}
                      onChange={event => setDeposit({...deposit, amount_rub: event.target.value})}
                    />
                  </Field>
                  <Field>
                    Причина удержания части
                    <Input
                      value={deposit.reason}
                      onChange={event => setDeposit({...deposit, reason: event.target.value})}
                      placeholder="Обязательна при частичном возврате"
                    />
                  </Field>
                </FieldGrid>
                <Actions>
                  <ActionButton
                    primary
                    disabled={saving || !deposit.amount_rub}
                    onClick={() => runAction(
                      'deposit-refund',
                      {
                        amount: Math.round(Number(deposit.amount_rub) * 100),
                        reason: deposit.reason.trim() || null,
                      },
                      'Вернуть указанную сумму депозита клиенту?'
                    )}
                  >
                    Вернуть депозит
                  </ActionButton>
                  <ActionButton
                    disabled={saving}
                    onClick={() => setDeposit({
                      ...deposit,
                      amount_rub: String((rental.deposit_amount
                        - rental.deposit_refunded_amount
                        - rental.deposit_withheld_amount) / 100),
                    })}
                  >
                    Подставить остаток
                  </ActionButton>
                  <ActionButton
                    danger
                    disabled={saving || !deposit.reason.trim()}
                    onClick={() => runAction(
                      'deposit-forfeit',
                      {reason: deposit.reason.trim()},
                      'Удержать весь остаток депозита в пользу компании? Деньги клиенту не вернутся.'
                    )}
                  >
                    Удержать полностью
                  </ActionButton>
                </Actions>
              </Section>
            )}


            <Section>
              <CardTitle>Дополнительные начисления</CardTitle>
              {(!rental.charges || rental.charges.length === 0) && <State>Начислений пока нет.</State>}
              {rental.charges && rental.charges.length > 0 && (
                <Scroll><Table minWidth="920px"><thead><tr><th>Дата</th><th>Категория</th><th>Комментарий</th><th>Сумма</th><th>Откуда</th><th>Статус</th><th>Доказательства</th><th>Действие</th></tr></thead>
                  <tbody>{rental.charges.map(item => <tr key={item.id}><td>{formatDate(item.created_at)}</td><td>{chargeCategories[item.category] || item.category}</td><td>{item.comment}</td><td>{money(item.amount)}</td><td>{item.source === 'deposit' ? 'Депозит' : 'Карта'}</td><td><Badge danger={item.status === 'debt'} success={item.status === 'paid'}>{item.status}</Badge></td><td>{(item.file_ids || []).map(fileId => <ProtectedFileButton key={fileId} authorization={authorization} fileId={fileId} />)}</td><td>{item.status === 'debt' && item.source !== 'deposit' && <InlineButton type="button" disabled={saving} onClick={() => retryCharge(item.id)}>Повторить списание</InlineButton>}</td></tr>)}</tbody>
                </Table></Scroll>
              )}

              <ChargeForm onSubmit={createCharge}>
                <CardTitle>Новое начисление</CardTitle>
                <FieldGrid>
                  <Field>Категория *<Select value={charge.category} onChange={event => { setCharge({...charge, category: event.target.value}); commandKeys.current.charge = ''; }}>{Object.keys(chargeCategories).map(category => <option key={category} value={category}>{chargeCategories[category]}</option>)}</Select></Field>
                  <Field>Сумма, ₽ *<Input required type="number" min="0.01" step="0.01" value={charge.amount_rub} onChange={event => { setCharge({...charge, amount_rub: event.target.value}); commandKeys.current.charge = ''; }} /></Field>
                </FieldGrid>
                <Field style={{marginTop: 14}}>Комментарий *<Textarea required short value={charge.comment} onChange={event => { setCharge({...charge, comment: event.target.value}); commandKeys.current.charge = ''; }} /></Field>
                <Field style={{marginTop: 14}}>Фото/скан доказательства *<Input required type="file" accept="image/jpeg,image/png" onChange={event => { setCharge({...charge, file: event.target.files[0] || null}); commandKeys.current.charge = ''; }} /></Field>

                <Field style={{marginTop: 14}}>
                  Откуда гасим *
                  <Select
                    value={charge.source}
                    onChange={event => {
                      setCharge({...charge, source: event.target.value});
                      commandKeys.current.charge = '';
                    }}
                  >
                    <option value="deposit">
                      Из депозита — свободно {money(depositFree)}
                    </option>
                    <option value="card">С привязанной карты</option>
                  </Select>
                </Field>
                <Hint>
                  Зачёт из депозита денег не двигает: депозит уже удержан, уменьшается
                  возвращаемая клиенту часть. Списание с карты — новая операция, и она
                  может не пройти: тогда сумма станет задолженностью.
                </Hint>
                {charge.source === 'deposit' && depositFree <= 0 && (
                  <ErrorText>
                    Свободного депозита нет — выберите списание с карты.
                  </ErrorText>
                )}
                {charge.source === 'deposit'
                  && depositFree > 0
                  && Math.round(Number(charge.amount_rub) * 100) > depositFree && (
                  <ErrorText>
                    Сумма больше свободного остатка депозита {money(depositFree)}.
                  </ErrorText>
                )}

                <Actions>
                  <ActionButton
                    type="submit"
                    danger
                    disabled={saving
                      || !charge.amount_rub
                      || !charge.comment.trim()
                      || !charge.file
                      || (charge.source === 'deposit'
                        && (depositFree <= 0
                          || Math.round(Number(charge.amount_rub) * 100) > depositFree))}
                  >
                    {charge.source === 'deposit' ? 'Зачесть из депозита' : 'Создать и списать'}
                  </ActionButton>
                </Actions>
              </ChargeForm>
            </Section>

            {isDevApi && testTools && (
              <TestSection>
                <CardTitle>Тестовые инструменты <TestBadge>только dev-стенд</TestBadge></CardTitle>
                <Hint>
                  Прямая правка полей в базе без бизнес-логики и без обращения к эквайеру:
                  автомобиль, депозит и напоминания не пересчитываются. Ограничения базы
                  (пересечение периодов, вторая активная аренда клиента) остаются в силе.
                </Hint>
                <FieldGrid style={{marginTop: 14}}>
                  <Field>
                    Статус аренды
                    <Select value={override.status} onChange={event => setOverride({...override, status: event.target.value})}>
                      {testTools.rental_statuses.map(status => (
                        <option key={status} value={status}>{rentalStatuses[status] || status} ({status})</option>
                      ))}
                    </Select>
                  </Field>
                  <Field>
                    Статус оплаты
                    <Select value={override.payment_status} onChange={event => setOverride({...override, payment_status: event.target.value})}>
                      {testTools.payment_statuses.map(status => (
                        <option key={status} value={status}>{paymentStatuses[status] || status} ({status})</option>
                      ))}
                    </Select>
                  </Field>
                  <Field>
                    Начало аренды
                    <Input type="datetime-local" value={override.starts_at} onChange={event => setOverride({...override, starts_at: event.target.value})} />
                  </Field>
                  <Field>
                    Окончание аренды
                    <Input type="datetime-local" value={override.ends_at} onChange={event => setOverride({...override, ends_at: event.target.value})} />
                  </Field>
                </FieldGrid>
                <Actions>
                  <ActionButton danger disabled={saving} onClick={applyOverride}>Применить изменения</ActionButton>
                </Actions>
                {rental.payments && rental.payments.length > 0 && (
                  <>
                    <Hint style={{marginTop: 18}}>Отмена платежа меняет только запись в базе: деньги у эквайера не возвращаются.</Hint>
                    <Scroll><Table minWidth="640px"><thead><tr><th>Дата</th><th>Назначение</th><th>Статус</th><th>Сумма</th><th></th></tr></thead>
                      <tbody>{rental.payments.map(payment => (
                        <tr key={`test-${payment.id}`}>
                          <td>{formatDate(payment.created_at)}</td>
                          <td>{payment.purpose}</td>
                          <td>{payment.status}</td>
                          <td>{money(payment.amount)}</td>
                          <td>
                            {payment.status !== 'canceled' && (
                              <InlineButton type="button" disabled={saving} onClick={() => cancelPaymentForTest(payment.id)}>
                                Отменить платёж
                              </InlineButton>
                            )}
                          </td>
                        </tr>
                      ))}</tbody>
                    </Table></Scroll>
                  </>
                )}
              </TestSection>
            )}

            {error && <ErrorText role="alert">{error}</ErrorText>}
            {message && <SuccessText aria-live="polite">{message}</SuccessText>}
          </>
        )}
      </Card>
    </Grid>
  );
}

export default function Rentals() {
  return (
    <Layout
      title="Аренды"
      description="Жизненный цикл аренды, платежи, возврат, осмотр и начисления с защищёнными доказательствами."
    >
      <RentalsContent />
    </Layout>
  );
}

const RentalMeta = styled.div`margin: 5px 0 8px; color: #666b73; font-size: 12px;`;
const ClientLink = styled.a`color: #0a4eb3; text-decoration: underline;`;
const HeaderBadges = styled.div`display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 20px;`;
const Section = styled.section`margin-top: 28px; padding-top: 24px; border-top: 1px solid #e6eaed;`;
const TestSection = styled.section`
  margin-top: 28px;
  padding: 20px;
  border: 1px dashed #e58a3c;
  border-radius: 10px;
  background: #fff8f1;
`;
const TestBadge = styled.span`
  display: inline-block;
  margin-left: 10px;
  padding: 2px 8px;
  border-radius: 999px;
  color: #9a4d00;
  background: #ffe2c2;
  font-size: 11px;
  font-weight: 700;
  vertical-align: middle;
`;
const NextStep = styled.section`
  margin: 0 0 24px;
  padding: 20px;
  border: 1px solid #bcd8ff;
  border-radius: 10px;
  background: #f4f9ff;
`;
const NextStepPart = styled.div`
  margin-top: 16px;
  &:first-of-type { margin-top: 0; }
`;
const MapBox = styled.div`
  height: 240px;
  border: 1px solid #cad0d9;
  border-radius: 8px;
  overflow: hidden;
`;
const MapHelp = styled.div`margin-top: 8px; color: #818286; font-size: 12px;`;
const Hint = styled.p`margin: 8px 0 0; color: #818286; font-size: 12px; line-height: 1.5;`;
const ChargeForm = styled.form`margin-top: 24px; padding: 20px; border: 1px solid #e6eaed; border-radius: 8px; background: #f9fafb;`;
const TimelineList = styled.ol`list-style: none; margin: 0; padding: 0;`;
const TimelineItem = styled.li`
  display: flex; gap: 12px; position: relative; min-height: 54px; color: #2f3033;
  &:not(:last-child)::after { content: ''; position: absolute; left: 6px; top: 17px; bottom: 0; width: 2px; background: #dce8f8; }
  small { display: block; margin-top: 4px; color: #818286; }
`;
const TimelineDot = styled.span`width: 14px; height: 14px; flex: 0 0 14px; margin-top: 3px; border-radius: 50%; background: #2e83ff;`;
const InlineButton = styled.button`
  min-height: 36px; padding: 7px 10px; border: 1px solid #2e83ff; border-radius: 4px; color: #0a4eb3; background: #fff; cursor: pointer;
  &:focus { outline: 3px solid #e1eeff; }
  &:disabled { opacity: .48; cursor: default; }
`;
const InlineError = styled.span`display: block; margin-top: 4px; color: #df2d14; font-size: 12px;`;
